import { useReprocessManifestCriticalGeometry } from '@/hooks/api/assets/useReprocessManifestCriticalGeometry';
import { usePutCriticalGeometry } from '@/hooks/api/assets/usePutCriticalGeometry';
import { RefObject, useCallback, useEffect, useState } from 'react';
import { AssetType } from '@physna/core/domain/asset/asset-type';
import {
  CriticalGeometryMessageType,
  isCriticalGeometryReleaseVectorMessage,
  isCriticalGeometryRequestUpdateMessage,
} from '@physna/core/domain/viewer/critical-geometry-messaging';
import { getIframeMessagingContext, IframeMessagingContext } from './getIframeMessagingContext';

export function useCriticalGeometryPut({
  assetId,
  tenantId,
  iframeRef,
  assetType,
}: {
  assetId?: string;
  tenantId?: string;
  iframeRef: RefObject<HTMLIFrameElement | null>;
  assetType?: string;
}) {
  const { reprocessManifestCriticalGeometryAsync } = useReprocessManifestCriticalGeometry();
  const { putCriticalGeometryAsync } = usePutCriticalGeometry();
  const [isPutInFlight, setIsPutInFlight] = useState(false);

  const requestSelectedReleaseUnitVector = useCallback(
    async (parameters: IframeMessagingContext): Promise<[number, number, number] | undefined> => {
      const { iframeWindow, targetOrigin } = parameters;
      const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

      return await new Promise(resolve => {
        const timeout = globalThis.setTimeout(() => {
          globalThis.removeEventListener('message', onMessage);
          resolve(void 0);
        }, 750);

        const onMessage = (event: MessageEvent) => {
          if (event.source !== iframeWindow || event.origin !== targetOrigin) return;

          const data = event.data as unknown;
          if (!isCriticalGeometryReleaseVectorMessage(data)) return;
          if (data.requestId !== requestId) return;

          globalThis.clearTimeout(timeout);
          globalThis.removeEventListener('message', onMessage);

          const vec = data.releaseUnitVector;
          const normalized =
            Array.isArray(vec) &&
            vec.length === 3 &&
            vec.every((v: unknown) => typeof v === 'number' && Number.isFinite(v))
              ? (vec as [number, number, number])
              : undefined;
          resolve(normalized);
        };

        globalThis.addEventListener('message', onMessage);
        iframeWindow.postMessage({ type: CriticalGeometryMessageType.GetReleaseVector, requestId }, targetOrigin);
      });
    },
    []
  );

  const postUpdateStateToIframe = useCallback(
    (isUpdating: boolean) => {
      const messagingContext = getIframeMessagingContext(iframeRef);
      if (!messagingContext) return;

      messagingContext.iframeWindow.postMessage(
        { type: CriticalGeometryMessageType.UpdateState, isUpdating },
        messagingContext.targetOrigin
      );
    },
    [iframeRef]
  );

  const put = useCallback(async () => {
    if (!assetId || !tenantId) return;
    if (isPutInFlight) return;
    setIsPutInFlight(true);

    try {
      const messagingContext = getIframeMessagingContext(iframeRef);

      if (assetType === AssetType.MANIFEST) {
        await reprocessManifestCriticalGeometryAsync({ tenantId, assetId });
      } else {
        const releaseUnitVector = messagingContext
          ? await requestSelectedReleaseUnitVector(messagingContext)
          : undefined;
        await putCriticalGeometryAsync({ tenantId, assetId, releaseUnitVector });
      }

      if (messagingContext) {
        messagingContext.iframeWindow.postMessage(
          { type: CriticalGeometryMessageType.ReloadAnalysis },
          messagingContext.targetOrigin
        );
      }
    } finally {
      setIsPutInFlight(false);
    }
  }, [
    assetId,
    assetType,
    iframeRef,
    isPutInFlight,
    putCriticalGeometryAsync,
    reprocessManifestCriticalGeometryAsync,
    requestSelectedReleaseUnitVector,
    tenantId,
  ]);

  useEffect(() => {
    postUpdateStateToIframe(isPutInFlight);
  }, [isPutInFlight, postUpdateStateToIframe]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!isCriticalGeometryRequestUpdateMessage(event.data)) return;

      const messagingContext = getIframeMessagingContext(iframeRef);
      if (!messagingContext) return;
      if (event.source !== messagingContext.iframeWindow || event.origin !== messagingContext.targetOrigin) return;

      void put();
    };

    globalThis.addEventListener('message', onMessage);
    return () => globalThis.removeEventListener('message', onMessage);
  }, [iframeRef, put]);

  return { put, isPutInFlight };
}
